import { styled } from '@mui/system';
import { Container, Item } from 'Components/Grid';
import { exists, partialDateToValue, sortPartialDate } from 'neuro-utils';
import { flatten } from 'ramda';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { useAppDispatch as useDispatch } from 'Store/index';
import { formatPartialDate } from 'Utility/date';
import { filterDocumentsByDateRequirement } from 'Utility/documentHandling';
import { path } from 'Utility/ramdaReplacement';
import Tooltip from '../../components/ToolTip';
import colors from '../../config/theme/colors';
import { styleDocument } from '../../config/theme/componentTheme';
import { actions } from '../../store/form';
import { appendIDs } from '../../utility/appendIDs';
import ActionButton from '../ActionButton';

const StyledDiv = styled('div')({
  margin: `0 -${styleDocument.padding}`,
  padding: `3rem ${styleDocument.padding}`,
  borderTop: '1px solid #c8c8c8',
});

const StyledHeader = styled('div')({
  color: colors.primary,
  fontSize: '2rem',
  fontWeight: 600,
  marginBottom: '1rem',
});

const PreviousValueText = styled('div')({
  fontSize: '1.4rem',
  color: colors.secondaryText,
});

const PreviousValueDate = styled('span')({
  fontSize: '1.4rem',
  color: colors.secondaryText,
});

const displayPreviousValue = (
  prevValue?: TPrevValue,
  optionFormatter?: (s: string, field?: string) => React.JSX.Element | string,
  saveSingleValue?: (name: string) => () => void,
): React.JSX.Element | undefined =>
  prevValue ? (
    <PreviousValueText>
      <React.Fragment>
        <div style={{ fontWeight: 600 }}>
          {exists(prevValue.value) ? (
            <Tooltip
              description={
                prevValue.date ? <PreviousValueDate>{formatPartialDate(prevValue.date)}</PreviousValueDate> : ''
              }
              content={
                <span onClick={saveSingleValue && saveSingleValue(prevValue.name)}>
                  {optionFormatter?.(prevValue.value, prevValue.name) ?? prevValue.value}
                </span>
              }
              hover={true}
            />
          ) : (
            ''
          )}
        </div>
      </React.Fragment>
    </PreviousValueText>
  ) : undefined;

const RenderWithHistory = ({
  element,
  prevValue,
  optionFormatter,
  hideHistory,
  commitsLength,
  saveSingleValue,
}: {
  element?: React.JSX.Element;
  prevValue?: TPrevValue;
  optionFormatter?: IOwnProps['optionFormatter'];
  hideHistory?: boolean;
  commitsLength?: number;
  saveSingleValue?: (name: string) => () => void;
}): React.JSX.Element => (
  <Container>
    <Item xs={true}>{element}</Item>
    <Item xs={2} style={{ marginTop: '0.7rem' }}>
      {!hideHistory && (commitsLength ?? 0) <= 1 && (
        <Container justifyContent="flex-start">
          <Item>{displayPreviousValue(prevValue, optionFormatter, saveSingleValue)}</Item>
        </Container>
      )}
    </Item>
  </Container>
);

const RenderElement = ({
  el,
  name,
  prevValues,
  optionFormatter,
  hideCopyButton,
  commitsLength,
  saveSingleValue,
}: {
  el: IElement;
  name: string;
  prevValues: TPrevValue[];
  commitsLength: number;
  saveSingleValue: (name: string) => () => void;
} & Partial<IOwnProps>): React.JSX.Element | null => {
  return el.element && el.condition !== false ? (
    <RenderWithHistory
      {...{
        element: el.element,
        prevValue: prevValues.find(p => p.name === name),
        optionFormatter: el.optionFormatter ?? optionFormatter,
        hideCopyButton,
        commitsLength,
        saveSingleValue,
      }}
    />
  ) : el.header && el.condition !== false ? (
    el.header
  ) : null;
};

const FormSectionHistoryAcceptor = ({
  documents,
  formData,
  optionFormatter,
  header,
  condition = true,
  headerRef = undefined,
  children,
  documentID,
  hideCopyButton = false,
  name,
  prevDocOnly = false,
  prevDocWithField = false,
}: IOwnProps): React.JSX.Element | null => {
  // Previous values based on date
  const [prevValues, setPrevValues] = React.useState<TPrevValue[]>([]);

  const childrenIdValuePairs = React.useRef(
    appendIDs(Object.keys(children).map(key => path([key, 'element', 'props', 'name'], children) ?? key)),
  );

  const commitsLength = documents?.find(d => d._id === documentID)?._commitsLength ?? 0;

  React.useEffect(() => {
    if (formData.document.date) {
      // All documents previous to editing documents date
      let previousDocs = documents
        ? filterDocumentsByDateRequirement(documents).filter(
            d => partialDateToValue(d.date) < partialDateToValue(formData.document.date),
          )
        : [];

      if (previousDocs.length === 0) {
        setPrevValues([]);
        return;
      }

      // Filter documents being edited (current form basically)
      previousDocs = previousDocs.filter(d => !d._editing);

      // Sort by date
      previousDocs.sort((n1, n2) => sortPartialDate(n1.date, n2.date)).reverse();

      // Takes in account only the latest document
      if (prevDocOnly && previousDocs[0]) {
        previousDocs = [previousDocs[0]];
      }

      const valueArray = [] as TPrevValue[];

      // Fetch field names from inside groups
      const groupNames = Object.keys(children).map(c =>
        children[c]?.group ? Object.keys(children[c].group.children) : null,
      );
      const filteredGroupNames = flatten(groupNames.filter(n => n !== null)) as string[];

      // Get previous values for all children
      let names: string[] = Object.keys(children).map(key => path([key, 'element', 'props', 'name'], children) ?? key);
      names = [...names, ...filteredGroupNames];

      // Takes in account only the latest document with some of the fields filled
      // Useful if FormSectionHistoryAcceptor has multiple InputHandlers inside
      if (prevDocWithField) {
        const prevDocWithSomeOfField = previousDocs.find(d => names.some(n => n in d && d[n]));
        previousDocs = prevDocWithSomeOfField ? [prevDocWithSomeOfField] : [];
      }

      names.forEach(n => {
        // Get first occurance of the value from previous docs
        previousDocs.some(d => {
          // Access nested property if given
          if (name) {
            if (exists(path(name.split('.').concat(n), d))) {
              const value = path(name.split('.').concat(n), d);
              valueArray.push({ name: n, date: d.date ?? undefined, value });
              return true;
            } else return false;
          } else if (exists(d[n]) || exists(path(n.split('.'), d))) {
            valueArray.push({ name: n, date: d.date ?? undefined, value: d[n] ?? path(n.split('.'), d) });
            return true;
          } else return false;
        });
      });
      setPrevValues(valueArray);
    } else {
      setPrevValues([]);
    }
  }, [formData.document.date, children, documents, name]);

  const dispatch = useDispatch();

  const mergeDeep = (target: TAnyObject, source: TAnyObject): TAnyObject => {
    const output = { ...target };
    for (const key in source) {
      if (
        source[key] instanceof Object &&
        key in target &&
        target[key] instanceof Object &&
        !Array.isArray(target[key])
      ) {
        // Recursively merge if both target and source are objects
        output[key] = mergeDeep(target[key] as TAnyObject, source[key] as TAnyObject);
      } else {
        // Direct assignment if source is not an object or target lacks the key
        output[key] = source[key];
      }
    }
    return output;
  };

  const constructNestedProperty = (path: string, value: any): TAnyObject => {
    const pathSplit = path.split('.').reverse();
    return pathSplit.reduce((prev, current, index) => ({ [current]: index === 0 ? value : { ...prev } }), {});
  };

  const saveValues = (): void => {
    let values: TAnyObject = {};
    prevValues.forEach(p => {
      const nestedValue = p.name.includes('.') ? constructNestedProperty(p.name, p.value) : { [p.name]: p.value };
      values = mergeDeep(values, nestedValue);
    });

    // Construct and merge final nested property if 'name' is provided
    if (name) {
      values = mergeDeep(values, constructNestedProperty(name, values));
    }

    // Dispatch the result
    documentID && dispatch(actions.updateFormValuesBatch(values, documentID));
  };

  const saveSingleValue = (name: string) => (): void => {
    const value = prevValues.find(v => v.name === name);
    if (value && documentID) dispatch(actions.updateFormValues(documentID, { [name]: value.value }));
  };

  return condition ? (
    <StyledDiv ref={headerRef}>
      <StyledHeader>
        <Container>
          <Item xs={10}>{header && <FormattedMessage id={header} />}</Item>

          {prevValues.length > 0 && (
            <Item xs={2}>
              <Container justifyContent="flex-start">
                {!hideCopyButton && commitsLength <= 1 && (
                  <Item>
                    <ActionButton
                      text="general.copyPrevious"
                      onClick={saveValues}
                      width={16}
                      height={3}
                      fontSize={14}
                    />
                  </Item>
                )}
              </Container>
              <Container
                justifyContent="flex-start"
                style={{ color: colors.secondaryText, fontSize: '1.4rem', marginTop: '1rem', fontWeight: 1 }}
              >
                {!hideCopyButton && commitsLength <= 1 && (
                  <React.Fragment>
                    <Item xs={5}>
                      <FormattedMessage id="general.previousData" />
                      {':'}
                    </Item>
                    <Item xs={7}>
                      <div style={{ marginLeft: '1rem' }}>
                        {prevValues[0]?.date ? ` ${formatPartialDate(prevValues[0].date)}` : ''}
                      </div>
                    </Item>
                  </React.Fragment>
                )}
              </Container>
            </Item>
          )}
        </Container>
      </StyledHeader>

      {Array.isArray(childrenIdValuePairs.current) &&
        childrenIdValuePairs.current.map(k => {
          const kValue = typeof k.value === 'string' ? k.value : undefined;
          const child = kValue ? children[kValue] : undefined;
          if (!kValue || !child) return <></>;
          const childGroup = child.group;
          return (
            <React.Fragment key={k.id}>
              {childGroup && childGroup.condition !== false ? (
                childGroup.groupElement(
                  <React.Fragment>
                    {Boolean(childGroup.children) &&
                      Object.keys(childGroup.children).map(c => {
                        const element = childGroup.children[c];
                        if (!element) return;
                        return (
                          <React.Fragment key={c}>
                            <RenderElement
                              {...{
                                el: element,
                                name: c,
                                prevValues,
                                optionFormatter,
                                commitsLength,
                                saveSingleValue,
                              }}
                            />
                          </React.Fragment>
                        );
                      })}
                  </React.Fragment>,
                )
              ) : (
                <RenderElement
                  {...{
                    el: child,
                    name: kValue,
                    prevValues,
                    optionFormatter,
                    commitsLength,
                    saveSingleValue,
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
    </StyledDiv>
  ) : null;
};

type TPrevValue = { name: string; date?: PartialDate; value?: any };

export interface IElement {
  group?: IGroupElement;
  element?: React.JSX.Element; // Field element
  optionFormatter?: (s: string, field?: string) => React.JSX.Element;
  header?: React.JSX.Element; // Header element in case we just want to render a text without other stuff
  condition?: boolean; // If false dont render anything
}

interface IGroupElement {
  groupElement: (elem: React.JSX.Element | undefined) => React.JSX.Element;
  children: { [key: string]: IElement };
  condition?: boolean;
}

interface IOwnProps {
  documents?: IDocumentV2[];
  documentID?: string;
  formData: IFormData<{ [key: string]: any; date?: PartialDate | null }>;
  children: {
    [key: string]: IElement;
  };
  optionFormatter?: (s: string, field?: string) => React.JSX.Element | string;
  header?: string;
  /** Whether to show this section at all, default true */
  condition?: boolean; // Can hide whole section
  headerRef?: React.RefObject<HTMLDivElement>;
  hideCopyButton?: boolean;
  /** Path for nested data structures */
  name?: string;
  // Finds values of the latest document only
  prevDocOnly?: boolean;
  // Finds the values of the latest document with some of the fields in FormSectionHistoryAcceptors children
  prevDocWithField?: boolean;
}

export default FormSectionHistoryAcceptor;
